import type { SavedView, TaskListQuery, TaskPriority, TaskStatus, TaskSummary } from '@pm/schemas';
import {
  Badge,
  Button,
  cn,
  Counter,
  EmptyState,
  ErrorState,
  Input,
  Kbd,
  LabelCaps,
  Menu,
  MenuCheckboxItem,
  MenuContent,
  MenuItem,
  MenuLabel,
  MenuSeparator,
  MenuTrigger,
  PageHeader,
  PriorityBadge,
  Segmented,
  SkeletonRows,
  StatusBadge,
} from '@pm/ui';
import {
  flexRender,
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type VisibilityState,
} from '@tanstack/react-table';
import {
  ArrowDown,
  ArrowUp,
  ArrowUpDown,
  Bookmark,
  Columns3,
  Filter,
  Inbox,
  Kanban,
  List,
  Plus,
  Search,
  Trash2,
  X,
} from 'lucide-react';
import { useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { UserPicker } from '@/components/pickers';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDate, PRIORITY_LABEL, STATUS_LABEL } from '@/lib/format';
import { keys, useInvalidating, useTasks, useViews } from '@/lib/queries';
import { useToast } from '@/lib/toast';
import {
  DueCell,
  ParticipantsCell,
  ResponsibleCell,
  SubtasksIndicator,
  TagsCell,
  TaskTitle,
} from './task-bits';
import { TaskCreateDialog } from './TaskCreateDialog';

type SortField = NonNullable<TaskListQuery['sort']>;
type GroupBy = 'none' | 'status' | 'priority' | 'context' | 'responsible';

const STATUSES: TaskStatus[] = [
  'NOT_STARTED',
  'IN_PROGRESS',
  'WAITING',
  'BLOCKED',
  'PENDING_APPROVAL',
  'COMPLETED',
  'CANCELED',
];
const PRIORITIES: TaskPriority[] = ['URGENT', 'HIGH', 'NORMAL', 'LOW'];
const OPEN: TaskStatus[] = ['NOT_STARTED', 'IN_PROGRESS', 'WAITING', 'BLOCKED', 'PENDING_APPROVAL'];

interface ViewConfig {
  status?: TaskStatus[];
  priority?: TaskPriority[];
  responsible_user_id?: string;
  overdue?: boolean;
  sort?: SortField;
  dir?: 'asc' | 'desc';
  groupBy?: GroupBy;
  columns?: VisibilityState;
}

/** Lista estilo Monday (FRONTEND_UX §5): filtros, ordenação, agrupamento, colunas e views salvas. */
export function TasksListPage() {
  const navigate = useNavigate();
  const toast = useToast();
  const { can } = useAuth();
  const [params, setParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [q, setQ] = useState(params.get('q') ?? '');
  const [status, setStatus] = useState<TaskStatus[]>(
    () => (params.get('status')?.split(',').filter(Boolean) as TaskStatus[]) ?? OPEN,
  );
  const [priority, setPriority] = useState<TaskPriority[]>([]);
  const [responsible, setResponsible] = useState<string | null>(params.get('responsible') ?? null);
  const [overdue, setOverdue] = useState(params.get('overdue') === 'true');
  const [trashed, setTrashed] = useState(false);
  const [sort, setSort] = useState<SortField>('due_at');
  const [dir, setDir] = useState<'asc' | 'desc'>('asc');
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [columns, setColumns] = useState<VisibilityState>({
    created_at: false,
    completed_at: false,
  });
  const [page, setPage] = useState(1);
  const [activeView, setActiveView] = useState<string | null>(null);

  const query = useMemo<Partial<TaskListQuery>>(
    () => ({
      q: q || undefined,
      status: status.length ? status : undefined,
      priority: priority.length ? priority : undefined,
      responsible_user_id: responsible ?? undefined,
      overdue: overdue || undefined,
      trashed: trashed || undefined,
      sort,
      dir,
      page,
      per_page: 50,
    }),
    [q, status, priority, responsible, overdue, trashed, sort, dir, page],
  );
  const { data, isLoading, isError, error, refetch, isFetching } = useTasks(query);
  const views = useViews();

  const saveView = useInvalidating(
    (input: { name: string; config: ViewConfig }) =>
      api.post<SavedView>('/views', { name: input.name, config: input.config, entity: 'TASKS' }),
    [keys.views],
  );
  const deleteView = useInvalidating((id: string) => api.delete(`/views/${id}`), [keys.views]);

  function applyView(v: SavedView) {
    const c = v.config as ViewConfig;
    setStatus(c.status ?? OPEN);
    setPriority(c.priority ?? []);
    setResponsible(c.responsible_user_id ?? null);
    setOverdue(Boolean(c.overdue));
    setSort(c.sort ?? 'due_at');
    setDir(c.dir ?? 'asc');
    setGroupBy(c.groupBy ?? 'none');
    if (c.columns) setColumns(c.columns);
    setActiveView(v.id);
    setPage(1);
  }

  function resetFilters() {
    setQ('');
    setStatus(OPEN);
    setPriority([]);
    setResponsible(null);
    setOverdue(false);
    setTrashed(false);
    setActiveView(null);
    setPage(1);
    setParams({});
  }

  const toggleSort = (field: SortField) => {
    if (sort === field) setDir(dir === 'asc' ? 'desc' : 'asc');
    else {
      setSort(field);
      setDir('asc');
    }
  };

  const cols = useMemo<ColumnDef<TaskSummary>[]>(
    () => [
      {
        id: 'number',
        header: () => (
          <SortHeader label="#" field="number" sort={sort} dir={dir} onClick={toggleSort} />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-3">#{row.original.number}</span>
        ),
        size: 64,
      },
      {
        id: 'title',
        header: () => (
          <SortHeader label="Demanda" field="title" sort={sort} dir={dir} onClick={toggleSort} />
        ),
        cell: ({ row }) => (
          <span className="flex min-w-0 items-center gap-2">
            <TaskTitle task={row.original} className="min-w-0" />
            <SubtasksIndicator task={row.original} />
          </span>
        ),
        size: 360,
      },
      {
        id: 'context',
        header: 'Contexto',
        cell: ({ row }) => (
          <span className="truncate text-text-2">{row.original.context?.name ?? '—'}</span>
        ),
        size: 160,
      },
      {
        id: 'responsible',
        header: 'Responsável',
        cell: ({ row }) => <ResponsibleCell task={row.original} />,
        size: 180,
      },
      {
        id: 'participants',
        header: 'Participantes',
        cell: ({ row }) => <ParticipantsCell task={row.original} />,
        size: 120,
      },
      {
        id: 'stage',
        header: 'Etapa',
        cell: ({ row }) =>
          row.original.workflow?.stage ? (
            <Badge mono={false} color={row.original.workflow.stage.color ?? undefined}>
              {row.original.workflow.stage.name}
            </Badge>
          ) : (
            <span className="text-text-3">—</span>
          ),
        size: 120,
      },
      {
        id: 'status',
        header: () => (
          <SortHeader label="Status" field="status" sort={sort} dir={dir} onClick={toggleSort} />
        ),
        cell: ({ row }) => <StatusBadge status={row.original.status} />,
        size: 140,
      },
      {
        id: 'priority',
        header: () => (
          <SortHeader label="Prior." field="priority" sort={sort} dir={dir} onClick={toggleSort} />
        ),
        cell: ({ row }) => <PriorityBadge priority={row.original.priority} />,
        size: 90,
      },
      {
        id: 'due_at',
        header: () => (
          <SortHeader label="Prazo" field="due_at" sort={sort} dir={dir} onClick={toggleSort} />
        ),
        cell: ({ row }) => <DueCell task={row.original} />,
        size: 150,
      },
      {
        id: 'tags',
        header: 'Tags',
        cell: ({ row }) => <TagsCell task={row.original} />,
        size: 160,
      },
      {
        id: 'created_at',
        header: () => (
          <SortHeader
            label="Criado"
            field="created_at"
            sort={sort}
            dir={dir}
            onClick={toggleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-3">{fmtDate(row.original.created_at)}</span>
        ),
        size: 90,
      },
      {
        id: 'completed_at',
        header: () => (
          <SortHeader
            label="Concluído"
            field="completed_at"
            sort={sort}
            dir={dir}
            onClick={toggleSort}
          />
        ),
        cell: ({ row }) => (
          <span className="font-mono text-xs text-text-3">
            {fmtDate(row.original.completed_at)}
          </span>
        ),
        size: 100,
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sort, dir],
  );

  const rows = data?.data ?? [];
  const table = useReactTable({
    data: rows,
    columns: cols,
    getCoreRowModel: getCoreRowModel(),
    state: { columnVisibility: columns },
    onColumnVisibilityChange: setColumns,
  });

  const groups = useMemo(() => groupRows(rows, groupBy), [rows, groupBy]);
  const total = data?.meta.total ?? 0;
  const activeFilters = [
    q && `“${q}”`,
    status.length !== OPEN.length && `${status.length} status`,
    priority.length && `${priority.length} prioridades`,
    responsible && 'responsável',
    overdue && 'atrasadas',
    trashed && 'lixeira',
  ].filter(Boolean) as string[];

  return (
    <div className="flex flex-col gap-gap-lg">
      <PageHeader
        title="Demandas"
        subtitle={
          <span className="font-mono text-xs text-text-3">
            {total} {total === 1 ? 'demanda' : 'demandas'}
            {isFetching ? ' · atualizando…' : ''}
          </span>
        }
        actions={
          can('tasks.create') ? (
            <Button variant="primary" onClick={() => setCreateOpen(true)}>
              <Plus /> Nova demanda
            </Button>
          ) : null
        }
      />

      <div className="flex flex-wrap items-center gap-gap">
        <Input
          leading={<Search />}
          placeholder="Buscar por título ou número"
          value={q}
          onChange={(e) => (setQ(e.target.value), setPage(1))}
          className="w-[260px]"
        />
        <Menu>
          <MenuTrigger asChild>
            <Button variant="ghost" size="sm">
              <Filter /> Filtros{' '}
              {activeFilters.length ? <Counter>{activeFilters.length}</Counter> : null}
            </Button>
          </MenuTrigger>
          <MenuContent className="w-[260px]">
            <MenuLabel>Status</MenuLabel>
            {STATUSES.map((s) => (
              <MenuCheckboxItem
                key={s}
                checked={status.includes(s)}
                onCheckedChange={(v) => (
                  setStatus(v ? [...status, s] : status.filter((x) => x !== s)),
                  setPage(1)
                )}
                onSelect={(e) => e.preventDefault()}
              >
                {STATUS_LABEL[s]}
              </MenuCheckboxItem>
            ))}
            <MenuSeparator />
            <MenuLabel>Prioridade</MenuLabel>
            {PRIORITIES.map((p) => (
              <MenuCheckboxItem
                key={p}
                checked={priority.includes(p)}
                onCheckedChange={(v) => (
                  setPriority(v ? [...priority, p] : priority.filter((x) => x !== p)),
                  setPage(1)
                )}
                onSelect={(e) => e.preventDefault()}
              >
                {PRIORITY_LABEL[p]}
              </MenuCheckboxItem>
            ))}
            <MenuSeparator />
            <MenuCheckboxItem
              checked={overdue}
              onCheckedChange={(v) => (setOverdue(Boolean(v)), setPage(1))}
              onSelect={(e) => e.preventDefault()}
            >
              Só atrasadas
            </MenuCheckboxItem>
            <MenuCheckboxItem
              checked={trashed}
              onCheckedChange={(v) => (setTrashed(Boolean(v)), setPage(1))}
              onSelect={(e) => e.preventDefault()}
            >
              <Trash2 /> Lixeira
            </MenuCheckboxItem>
            <MenuSeparator />
            <MenuItem onSelect={resetFilters}>
              <X /> Limpar filtros
            </MenuItem>
          </MenuContent>
        </Menu>
        <div className="w-[200px]">
          <UserPicker
            value={responsible}
            onChange={(v) => (setResponsible(v), setPage(1))}
            placeholder="Qualquer responsável"
            size="sm"
          />
        </div>
        {activeFilters.map((f) => (
          <span
            key={f}
            className="inline-flex h-6 items-center gap-1 rounded-sm border border-accent-border bg-accent-soft px-2 text-sm text-accent"
          >
            {f}
          </span>
        ))}

        <div className="ml-auto flex items-center gap-gap">
          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="sm">
                Agrupar: {groupLabel(groupBy)}
              </Button>
            </MenuTrigger>
            <MenuContent>
              {(['none', 'status', 'priority', 'context', 'responsible'] as GroupBy[]).map((g) => (
                <MenuItem key={g} onSelect={() => setGroupBy(g)}>
                  {groupLabel(g)}
                </MenuItem>
              ))}
            </MenuContent>
          </Menu>
          <Menu>
            <MenuTrigger asChild>
              <Button variant="ghost" size="sm" aria-label="Colunas">
                <Columns3 /> Colunas
              </Button>
            </MenuTrigger>
            <MenuContent>
              {table.getAllLeafColumns().map((c) => (
                <MenuCheckboxItem
                  key={c.id}
                  checked={c.getIsVisible()}
                  onCheckedChange={(v) => c.toggleVisibility(Boolean(v))}
                  onSelect={(e) => e.preventDefault()}
                >
                  {typeof c.columnDef.header === 'string' ? c.columnDef.header : columnLabel(c.id)}
                </MenuCheckboxItem>
              ))}
            </MenuContent>
          </Menu>
          <Menu>
            <MenuTrigger asChild>
              <Button variant="secondary" size="sm">
                <Bookmark />{' '}
                {activeView
                  ? (views.data?.find((v) => v.id === activeView)?.name ?? 'View')
                  : 'Views'}
              </Button>
            </MenuTrigger>
            <MenuContent className="w-[240px]">
              <MenuLabel>Views salvas</MenuLabel>
              {(views.data ?? []).map((v) => (
                <MenuItem key={v.id} onSelect={() => applyView(v)} className="justify-between">
                  <span className="truncate">{v.name}</span>
                  {v.is_shared ? (
                    <span className="font-mono text-[10px] text-text-3">compartilhada</span>
                  ) : null}
                </MenuItem>
              ))}
              {(views.data ?? []).length === 0 ? (
                <p className="px-2 py-2 text-sm text-text-3">Nenhuma view ainda.</p>
              ) : null}
              <MenuSeparator />
              <MenuItem
                onSelect={() => {
                  const name = window.prompt('Nome da view');
                  if (!name) return;
                  saveView.mutate(
                    {
                      name,
                      config: {
                        status,
                        priority,
                        responsible_user_id: responsible ?? undefined,
                        overdue,
                        sort,
                        dir,
                        groupBy,
                        columns,
                      },
                    },
                    {
                      onSuccess: () => toast.success('View salva'),
                      onError: (e) => toast.error(e),
                    },
                  );
                }}
              >
                <Plus /> Salvar filtros atuais como view
              </MenuItem>
              {activeView ? (
                <MenuItem
                  danger
                  onSelect={() =>
                    deleteView.mutate(activeView, {
                      onSuccess: () => (setActiveView(null), toast.success('View removida')),
                    })
                  }
                >
                  <Trash2 /> Excluir view atual
                </MenuItem>
              ) : null}
            </MenuContent>
          </Menu>
          <Segmented
            ariaLabel="Visualização"
            value="list"
            onChange={(v) => v === 'kanban' && navigate('/pipelines')}
            options={[
              {
                value: 'list',
                label: (
                  <>
                    <List /> Lista
                  </>
                ),
              },
              {
                value: 'kanban',
                label: (
                  <>
                    <Kanban /> Quadro
                  </>
                ),
              },
            ]}
          />
        </div>
      </div>

      <div className="overflow-x-auto rounded-md border border-border bg-surface-1">
        {isLoading ? (
          <SkeletonRows rows={8} />
        ) : isError ? (
          <ErrorState message={error.message} onRetry={() => void refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="Nenhuma demanda por aqui"
            description={
              activeFilters.length
                ? 'Ajuste os filtros ou limpe a busca.'
                : 'Crie a primeira demanda para começar.'
            }
            action={
              can('tasks.create') && !activeFilters.length ? (
                <Button variant="primary" size="sm" onClick={() => setCreateOpen(true)}>
                  <Plus /> Nova demanda
                </Button>
              ) : undefined
            }
          />
        ) : (
          <table className="w-full min-w-[1180px] border-collapse">
            <thead>
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      style={{ width: h.getSize() }}
                      className="sticky top-0 h-8 whitespace-nowrap border-b border-border-strong bg-bg px-3 text-left font-mono text-xs font-medium uppercase tracking-[var(--tracking-caps)] text-text-3"
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody>
              {groups.map((g) => (
                <GroupRows
                  key={g.key}
                  group={g}
                  table={table}
                  colSpan={table.getVisibleLeafColumns().length}
                  grouped={groupBy !== 'none'}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {total > 50 ? (
        <div className="flex items-center justify-end gap-2 font-mono text-xs text-text-3">
          <Button variant="ghost" size="sm" disabled={page === 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          página {page} de {Math.ceil(total / 50)}
          <Button
            variant="ghost"
            size="sm"
            disabled={page * 50 >= total}
            onClick={() => setPage(page + 1)}
          >
            Próxima
          </Button>
        </div>
      ) : null}

      <p className="text-xs text-text-3">
        Dica: <Kbd>⌘K</Kbd> abre a busca global.
      </p>
      <TaskCreateDialog open={createOpen} onOpenChange={setCreateOpen} />
    </div>
  );
}

function SortHeader({
  label,
  field,
  sort,
  dir,
  onClick,
}: {
  label: string;
  field: SortField;
  sort: SortField;
  dir: 'asc' | 'desc';
  onClick: (f: SortField) => void;
}) {
  const active = sort === field;
  const Icon = active ? (dir === 'asc' ? ArrowUp : ArrowDown) : ArrowUpDown;
  return (
    <button
      type="button"
      onClick={() => onClick(field)}
      className={cn(
        'group inline-flex items-center gap-1 uppercase hover:text-text-1',
        active && 'text-accent',
      )}
    >
      {label}
      <Icon className={cn('size-3 opacity-0 group-hover:opacity-100', active && 'opacity-100')} />
    </button>
  );
}

interface Group {
  key: string;
  label: React.ReactNode;
  rows: TaskSummary[];
}

function groupRows(rows: TaskSummary[], by: GroupBy): Group[] {
  if (by === 'none') return [{ key: 'all', label: null, rows }];
  const map = new Map<string, Group>();
  for (const r of rows) {
    const [key, label] =
      by === 'status'
        ? [r.status, <StatusBadge key="s" status={r.status} />]
        : by === 'priority'
          ? [r.priority, PRIORITY_LABEL[r.priority]]
          : by === 'context'
            ? [r.context?.id ?? '—', r.context?.name ?? 'Sem contexto']
            : [r.responsible?.id ?? '—', r.responsible?.name ?? 'Não atribuída'];
    const g: Group = map.get(key) ?? { key, label, rows: [] };
    g.rows.push(r);
    map.set(key, g);
  }
  return [...map.values()];
}

function GroupRows({
  group,
  table,
  colSpan,
  grouped,
}: {
  group: Group;
  table: ReturnType<typeof useReactTable<TaskSummary>>;
  colSpan: number;
  grouped: boolean;
}) {
  const [open, setOpen] = useState(true);
  const rowModels = table.getRowModel().rows.filter((r) => group.rows.includes(r.original));
  return (
    <>
      {grouped ? (
        <tr className="bg-bg-elev">
          <td colSpan={colSpan} className="h-[30px] px-3">
            <button
              type="button"
              onClick={() => setOpen(!open)}
              className="inline-flex items-center gap-2"
            >
              <LabelCaps className="text-text-2">
                {typeof group.label === 'string' ? group.label : group.label}
              </LabelCaps>
              <Counter tone="neutral">{group.rows.length}</Counter>
            </button>
          </td>
        </tr>
      ) : null}
      {open
        ? rowModels.map((row) => (
            <tr
              key={row.id}
              className="h-row border-b border-border transition-colors duration-(--dur-fast) last:border-b-0 hover:bg-surface-2"
            >
              {row.getVisibleCells().map((cell) => (
                <td key={cell.id} className="whitespace-nowrap px-3 align-middle">
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </td>
              ))}
            </tr>
          ))
        : null}
    </>
  );
}

function groupLabel(g: GroupBy): string {
  return {
    none: 'Nenhum',
    status: 'Status',
    priority: 'Prioridade',
    context: 'Contexto',
    responsible: 'Responsável',
  }[g];
}

function columnLabel(id: string): string {
  return (
    {
      number: '#',
      title: 'Demanda',
      status: 'Status',
      priority: 'Prioridade',
      due_at: 'Prazo',
      created_at: 'Criado em',
      completed_at: 'Concluído em',
    }[id] ?? id
  );
}
